import { useEffect, useRef, useState, useCallback } from 'react'
import {
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'

const ANIM_MS = 220

/**
 * In-app document viewer.
 *
 * Shows a PDF (or any embeddable URL) inside a polished modal instead of
 * opening a new browser tab. Full-screen sheet on mobile, centered card on
 * desktop. Handles Esc-to-close, backdrop dismiss, body scroll lock, focus
 * management and prefers-reduced-motion.
 *
 * Props:
 *   open       - boolean, whether the viewer is shown
 *   onClose    - () => void
 *   url        - document URL to embed
 *   title      - main heading (e.g. subject label)
 *   subtitle   - secondary line (e.g. year / file type)
 *   kind       - 'exam' | 'solution' | 'secondary' (drives the accent chip)
 */
function DocumentViewerModal({ open, onClose, url, title, subtitle, kind = 'exam' }) {
  // Keep the node mounted through the exit animation.
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)
  const [iframeLoaded, setIframeLoaded] = useState(false)

  const panelRef = useRef(null)
  const closeBtnRef = useRef(null)
  const lastFocusedRef = useRef(null)

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  // Mount / unmount with enter+exit transition.
  useEffect(() => {
    let t
    if (open) {
      lastFocusedRef.current = document.activeElement
      setMounted(true)
      setIframeLoaded(false)
      // next frame -> flip to visible so the transition runs
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
    } else if (mounted) {
      setVisible(false)
      t = setTimeout(() => setMounted(false), prefersReducedMotion ? 0 : ANIM_MS)
    }
    return () => clearTimeout(t)
  }, [open, mounted, prefersReducedMotion])

  // Lock body scroll while open.
  useEffect(() => {
    if (!mounted) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [mounted])

  // Esc to close + focus the close button on open.
  useEffect(() => {
    if (!mounted) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    const focusT = setTimeout(() => closeBtnRef.current?.focus(), 60)
    return () => {
      document.removeEventListener('keydown', onKey)
      clearTimeout(focusT)
    }
  }, [mounted, onClose])

  // Restore focus to the trigger on close.
  useEffect(() => {
    if (!mounted && lastFocusedRef.current instanceof HTMLElement) {
      lastFocusedRef.current.focus?.()
    }
  }, [mounted])

  const handleBackdrop = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose?.()
    },
    [onClose]
  )

  // The HTML `download` attribute is ignored for cross-origin files, so for
  // Supabase storage URLs we ask the server to send the file as an attachment
  // via the `?download=<filename>` param (sets Content-Disposition). This works
  // identically in dev and prod without any JS fetch/CORS. External URLs are
  // left untouched (a plain link).
  const downloadUrl = (() => {
    if (!url) return ''
    try {
      const parsed = new URL(url, window.location.origin)
      if (parsed.pathname.includes('/storage/v1/object/public/')) {
        const name = decodeURIComponent(parsed.pathname.split('/').pop() || 'document.pdf')
        parsed.searchParams.set('download', name)
        return parsed.toString()
      }
    } catch {
      /* fall through */
    }
    return url
  })()

  if (!mounted) return null

  const kindMeta = {
    exam: { label: 'Θέματα', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    solution: { label: 'Λύσεις', dot: 'bg-indigo-500', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    secondary: { label: 'Αρχείο', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  }[kind] || { label: 'Αρχείο', dot: 'bg-gray-500', chip: 'bg-gray-100 text-gray-700 border-gray-200' }

  const embedSrc = url ? `${url}#view=FitH&toolbar=1` : ''
  const motion = prefersReducedMotion ? '' : 'transition-all duration-200 ease-out'

  return (
    <div
      className="fixed inset-0 z-[1000]"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Προβολή αρχείου'}
    >
      {/* Backdrop */}
      <div
        onClick={handleBackdrop}
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm ${motion} ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
      />

      {/* Panel wrapper (also handles outside-click on desktop) */}
      <div
        onClick={handleBackdrop}
        className="absolute inset-0 flex items-stretch sm:items-center sm:justify-center sm:p-4 md:p-6"
      >
        <div
          ref={panelRef}
          className={`relative flex w-full flex-col overflow-hidden bg-white shadow-2xl
            h-full sm:h-[90vh] sm:max-w-5xl sm:rounded-2xl sm:border sm:border-gray-200
            ${motion} ${
              visible
                ? 'opacity-100 translate-y-0 sm:scale-100'
                : 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-[0.98]'
            }`}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 sm:px-5 sm:py-4"
            style={{
              paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
            }}
          >
            <span
              className={`hidden sm:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white`}
              aria-hidden="true"
            >
              <DocumentTextIcon className="h-5 w-5" />
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="truncate text-sm font-extrabold text-gray-900 sm:text-base">
                  {title}
                </h2>
                <span
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${kindMeta.chip}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${kindMeta.dot}`} />
                  {kindMeta.label}
                </span>
              </div>
              {subtitle ? (
                <p className="mt-0.5 truncate text-xs text-gray-500">{subtitle}</p>
              ) : null}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5">
              {url ? (
                <>
                  <a
                    href={downloadUrl}
                    download
                    className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer transition"
                    aria-label="Λήψη αρχείου"
                    title="Λήψη"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </a>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-11 w-11 sm:h-9 sm:w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer transition"
                    aria-label="Άνοιγμα σε νέα καρτέλα"
                    title="Άνοιγμα σε νέα καρτέλα"
                  >
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                  </a>
                </>
              ) : null}
              <button
                ref={closeBtnRef}
                type="button"
                onClick={onClose}
                className="inline-flex h-11 w-11 sm:h-9 sm:w-9 items-center justify-center rounded-lg bg-gray-900 text-white hover:bg-gray-800 cursor-pointer transition"
                aria-label="Κλείσιμο"
                title="Κλείσιμο (Esc)"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="relative flex-1 bg-gray-100">
            {url ? (
              <>
                {!iframeLoaded && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div className="h-9 w-9 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                    <p className="text-sm font-medium text-gray-500">Φόρτωση αρχείου…</p>
                  </div>
                )}
                <iframe
                  src={embedSrc}
                  title={title || 'Έγγραφο'}
                  onLoad={() => setIframeLoaded(true)}
                  className={`h-full w-full ${motion} ${iframeLoaded ? 'opacity-100' : 'opacity-0'}`}
                />
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
                <p className="text-sm text-gray-500">Δεν βρέθηκε αρχείο για προβολή.</p>
              </div>
            )}
          </div>

          {/* Mobile helper bar */}
          {url ? (
            <div
              className="flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-2.5 sm:hidden"
              style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}
            >
              <p className="truncate text-xs text-gray-500">Δεν φορτώνει σωστά;</p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold text-gray-800 cursor-pointer"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Νέα καρτέλα
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default DocumentViewerModal
